import {
  APPROXIMATION_LIMIT_PERCENT,
  KW,
  MIN_STRONG_CONCENTRATION,
  NEUTRAL_PH,
  approximateIonised,
  exactIonised,
  formatLog,
  formatSci,
  pFrom,
  weakSpecies,
  type WeakApproximation,
} from './acidBaseCalculations';
import { type AcidBasePhSpec } from './acidBaseCalculationsData';
import { type ExploreChallenge, type ModelField, type WorkedStep, firstModelError } from './modelInputContract';

/**
 * Learner-driven acid–base Explore model (CT-INTERACT, chemistry/CLAUDE.md "Interactive
 * calculation standard"). It uses only the reviewed constants of acidBaseCalculations.ts:
 * Kw = 1.0 × 10⁻¹⁴ at 25 °C, the 1 × 10⁻⁶ mol L⁻¹ strong-solution limit, the 5 % rule and the
 * reviewed Ka/Kb values. The strong solutes are the ones already used in the reviewed problems.
 * reviewStatus: 'pending-teaching-review' (ACID_BASE_REVIEW_STATUS).
 */
export type AcidBaseExploreKind = 'strong' | 'dilution' | 'mixing' | 'weak';

export interface StrongSolute {
  id: string;
  formula: string;
  type: 'acid' | 'base';
  /** H₃O⁺ (acid) or OH⁻ (base) released per formula unit on complete ionisation. */
  ionsPerFormula: 1 | 2;
}

export const STRONG_SOLUTES: readonly StrongSolute[] = [
  { id: 'hcl', formula: 'HCl', type: 'acid', ionsPerFormula: 1 },
  { id: 'hno3', formula: 'HNO₃', type: 'acid', ionsPerFormula: 1 },
  { id: 'naoh', formula: 'NaOH', type: 'base', ionsPerFormula: 1 },
  { id: 'baoh2', formula: 'Ba(OH)₂', type: 'base', ionsPerFormula: 2 },
];

export type AcidBaseExploreInput =
  | 'concentration'
  | 'aliquotMl'
  | 'finalMl'
  | 'acidConcentration'
  | 'acidVolumeMl'
  | 'baseConcentration'
  | 'baseVolumeMl'
  | 'weakConcentration';

const CONCENTRATION_UNIT = 'mol/L';

export const ACID_BASE_EXPLORE_LIMITS: Record<AcidBaseExploreInput, Omit<ModelField, 'key'>> = {
  concentration: { label: 'Concentration', unit: CONCENTRATION_UNIT, min: MIN_STRONG_CONCENTRATION, max: 1, step: 0.000001 },
  aliquotMl: { label: 'Aliquot volume', unit: 'mL', min: 1, max: 250, step: 0.1 },
  finalMl: { label: 'Final volume', unit: 'mL', min: 1, max: 5000, step: 0.1 },
  acidConcentration: { label: 'Acid concentration', unit: CONCENTRATION_UNIT, min: MIN_STRONG_CONCENTRATION, max: 1, step: 0.001 },
  acidVolumeMl: { label: 'Acid volume', unit: 'mL', min: 1, max: 250, step: 0.1 },
  baseConcentration: { label: 'Base concentration', unit: CONCENTRATION_UNIT, min: MIN_STRONG_CONCENTRATION, max: 1, step: 0.001 },
  baseVolumeMl: { label: 'Base volume', unit: 'mL', min: 1, max: 250, step: 0.1 },
  weakConcentration: { label: 'Starting concentration', unit: CONCENTRATION_UNIT, min: 0.0001, max: 1, step: 0.0001 },
};

const INPUTS_BY_KIND: Record<AcidBaseExploreKind, AcidBaseExploreInput[]> = {
  strong: ['concentration'],
  dilution: ['concentration', 'aliquotMl', 'finalMl'],
  mixing: ['acidConcentration', 'acidVolumeMl', 'baseConcentration', 'baseVolumeMl'],
  weak: ['weakConcentration'],
};

/** The input fields one kind of calculation needs, for the shared input panel. */
export function acidBaseExploreFields(kind: AcidBaseExploreKind): ModelField<AcidBaseExploreInput>[] {
  return INPUTS_BY_KIND[kind].map((key) => ({ key, ...ACID_BASE_EXPLORE_LIMITS[key] }));
}

export interface AcidBaseExploreSetup {
  kind: AcidBaseExploreKind;
  /** Solute for the strong and dilution calculations. */
  strongId: string;
  acidId: string;
  baseId: string;
  weakId: string;
  values: Record<AcidBaseExploreInput, number>;
}

export const DEFAULT_ACID_BASE_EXPLORE: AcidBaseExploreSetup = {
  kind: 'strong',
  strongId: 'hcl',
  acidId: 'hcl',
  baseId: 'naoh',
  weakId: 'ethanoic',
  values: {
    concentration: 0.01,
    aliquotMl: 10,
    finalMl: 250,
    acidConcentration: 0.1,
    acidVolumeMl: 25,
    baseConcentration: 0.1,
    baseVolumeMl: 20,
    weakConcentration: 0.1,
  },
};

export interface AcidBaseExploreResult {
  /** The reaction that sets the ion concentration. */
  lead: string;
  steps: WorkedStep[];
  /** Null when the model stops at its limit; no pH is drawn then. */
  modelPH: number | null;
  /** Percentage ionisation of a weak species, for the ionisation grid. */
  percentIonised: number | null;
  approximation: WeakApproximation | null;
  weakSpeciesId: string | null;
  summary: string;
  limitNote: string | null;
}

const PERCENT = 100;
/** Relative difference below which the acid and base amounts count as exactly equal. */
const NEUTRAL_TOLERANCE = 1e-9;
const KW_TEXT = '1.0 × 10⁻¹⁴';
const LIMIT_TEXT = '1 × 10⁻⁶ mol L⁻¹';

const conc = (value: number) => `${formatSci(value)} mol/L`;

function strongSolute(id: string, type?: 'acid' | 'base'): StrongSolute {
  const solute = STRONG_SOLUTES.find((candidate) => candidate.id === id);
  if (!solute || (type && solute.type !== type)) throw new Error('Choose a reviewed strong acid or base.');
  return solute;
}

/** [H₃O⁺] from Kw for a base, the Kw cross-check for an acid, then pH = −log₁₀[H₃O⁺]. */
function phFromIon(ion: 'acid' | 'base', ionConcentration: number): { steps: WorkedStep[]; pH: number } {
  const hydronium = ion === 'acid' ? ionConcentration : KW / ionConcentration;
  const pH = pFrom(hydronium);
  const kwStep: WorkedStep =
    ion === 'acid'
      ? {
          id: 'kw',
          title: 'Use Kw for the other ion',
          formula: '[OH⁻] = Kw ÷ [H₃O⁺]',
          working: `[OH⁻] = ${KW_TEXT} ÷ ${formatSci(ionConcentration)} = ${conc(KW / ionConcentration)}`,
          status: 'done',
        }
      : {
          id: 'kw',
          title: 'Use Kw to find [H₃O⁺]',
          formula: '[H₃O⁺] = Kw ÷ [OH⁻]',
          working: `[H₃O⁺] = ${KW_TEXT} ÷ ${formatSci(ionConcentration)} = ${conc(hydronium)}`,
          status: 'done',
        };
  return {
    pH,
    steps: [
      kwStep,
      {
        id: 'ph',
        title: 'Find the pH',
        formula: 'pH = −log₁₀[H₃O⁺]',
        working: `pH = −log₁₀(${formatSci(hydronium)}) = ${formatLog(pH)}`,
        status: 'done',
      },
    ],
  };
}

const STOPPED_STEPS: WorkedStep[] = [
  { id: 'kw', title: 'Use Kw', formula: 'Kw = [H₃O⁺][OH⁻]', working: '', status: 'not-reached', note: 'model stopped' },
  { id: 'ph', title: 'Find the pH', formula: 'pH = −log₁₀[H₃O⁺]', working: '', status: 'not-reached', note: 'model stopped' },
];

function limitNoteFor(ion: string, value: number): string {
  return `[${ion}] = ${formatSci(value)} mol L⁻¹ is below ${LIMIT_TEXT}. Water’s own ions matter here, so the model stops instead of guessing a pH.`;
}

/** Finishes a strong-solution calculation from the ion concentration, stopping at the model limit. */
function finishStrong(
  ion: 'acid' | 'base',
  ionConcentration: number,
  lead: string,
  earlier: WorkedStep[],
): AcidBaseExploreResult {
  const ionName = ion === 'acid' ? 'H₃O⁺' : 'OH⁻';
  const base = { lead, percentIonised: null, approximation: null, weakSpeciesId: null };
  if (ionConcentration < MIN_STRONG_CONCENTRATION) {
    return {
      ...base,
      steps: [...earlier.map((step, index) => (index === earlier.length - 1 ? { ...step, status: 'current' as const } : step)), ...STOPPED_STEPS],
      modelPH: null,
      summary: 'Result: no pH. The model stops at its limit.',
      limitNote: limitNoteFor(ionName, ionConcentration),
    };
  }
  const { steps, pH } = phFromIon(ion, ionConcentration);
  return {
    ...base,
    steps: [...earlier, ...steps],
    modelPH: pH,
    summary: `Result: [${ionName}] = ${conc(ionConcentration)} · pH = ${formatLog(pH)}`,
    limitNote: null,
  };
}

function ionStep(solute: StrongSolute, concentration: number, symbol = 'c'): { step: WorkedStep; ions: number } {
  const ions = concentration * solute.ionsPerFormula;
  const ion = solute.type === 'acid' ? 'H₃O⁺' : 'OH⁻';
  const factor = solute.ionsPerFormula === 2 ? `2 × ${formatSci(concentration)}` : formatSci(concentration);
  return {
    ions,
    step: {
      id: 'ions',
      title: 'Complete ionisation',
      formula: solute.ionsPerFormula === 2 ? `[${ion}] = 2 × ${symbol}` : `[${ion}] = ${symbol}`,
      working: `[${ion}] = ${factor} = ${conc(ions)}`,
      status: 'done',
    },
  };
}

function ionisationEquation(solute: StrongSolute): string {
  if (solute.type === 'acid') return `${solute.formula} + H₂O → H₃O⁺ + ${solute.formula === 'HCl' ? 'Cl⁻' : 'NO₃⁻'} (complete)`;
  return solute.ionsPerFormula === 2 ? `${solute.formula} → Ba²⁺ + 2OH⁻ (complete)` : `${solute.formula} → Na⁺ + OH⁻ (complete)`;
}

function calculateStrong(setup: AcidBaseExploreSetup): AcidBaseExploreResult {
  const solute = strongSolute(setup.strongId);
  const { step, ions } = ionStep(solute, setup.values.concentration);
  return finishStrong(solute.type, ions, ionisationEquation(solute), [step]);
}

function calculateDilution(setup: AcidBaseExploreSetup): AcidBaseExploreResult {
  const solute = strongSolute(setup.strongId);
  const { concentration, aliquotMl, finalMl } = setup.values;
  if (finalMl < aliquotMl) throw new Error(`Final volume must be at least the aliquot volume (${aliquotMl} mL).`);
  const diluted = (concentration * aliquotMl) / finalMl;
  const dilute: WorkedStep = {
    id: 'dilute',
    title: 'Dilute',
    formula: 'c₂ = c₁ × V₁ ÷ V₂',
    working: `c₂ = ${formatSci(concentration)} mol/L × ${aliquotMl} mL ÷ ${finalMl} mL = ${conc(diluted)}`,
    status: 'done',
  };
  const { step, ions } = ionStep(solute, diluted, 'c₂');
  return finishStrong(solute.type, ions, ionisationEquation(solute), [dilute, step]);
}

function calculateMixing(setup: AcidBaseExploreSetup): AcidBaseExploreResult {
  const acid = strongSolute(setup.acidId, 'acid');
  const base = strongSolute(setup.baseId, 'base');
  const { acidConcentration, acidVolumeMl, baseConcentration, baseVolumeMl } = setup.values;
  const acidMmol = acidConcentration * acidVolumeMl;
  const hydroxideMmol = baseConcentration * baseVolumeMl * base.ionsPerFormula;
  const totalMl = acidVolumeMl + baseVolumeMl;
  const excess = acidMmol - hydroxideMmol;
  const lead = 'H₃O⁺ + OH⁻ → 2H₂O uses them up 1 : 1';
  const steps: WorkedStep[] = [
    {
      id: 'acid-amount',
      title: `Amount of H₃O⁺ from ${acid.formula}`,
      formula: 'n = c × V',
      working: `n(H₃O⁺) = ${formatSci(acidConcentration)} mol/L × ${acidVolumeMl} mL = ${formatSci(acidMmol)} mmol`,
      status: 'done',
    },
    {
      id: 'base-amount',
      title: `Amount of OH⁻ from ${base.formula}`,
      formula: base.ionsPerFormula === 2 ? 'n = c × V × 2' : 'n = c × V',
      working: `n(OH⁻) = ${formatSci(baseConcentration)} mol/L × ${baseVolumeMl} mL${base.ionsPerFormula === 2 ? ' × 2' : ''} = ${formatSci(hydroxideMmol)} mmol`,
      status: 'done',
    },
  ];
  if (Math.abs(excess) <= NEUTRAL_TOLERANCE * Math.max(acidMmol, hydroxideMmol)) {
    steps.push(
      {
        id: 'excess',
        title: 'Neutralise',
        formula: 'excess = n(H₃O⁺) − n(OH⁻)',
        working: `${formatSci(acidMmol)} − ${formatSci(hydroxideMmol)} = 0 mmol, so neither is in excess`,
        status: 'done',
      },
      {
        id: 'ph',
        title: 'Find the pH',
        formula: '[H₃O⁺] = √Kw',
        working: `[H₃O⁺] = √(${KW_TEXT}) = 1.0 × 10⁻⁷ mol/L, so pH = ${formatLog(NEUTRAL_PH)}`,
        status: 'done',
      },
    );
    return {
      lead,
      steps,
      modelPH: NEUTRAL_PH,
      percentIonised: null,
      approximation: null,
      weakSpeciesId: null,
      summary: `Result: exact neutralisation in ${totalMl} mL · pH = ${formatLog(NEUTRAL_PH)}`,
      limitNote: null,
    };
  }
  const acidExcess = excess > 0;
  const ion = acidExcess ? 'H₃O⁺' : 'OH⁻';
  const excessMmol = Math.abs(excess);
  const leftover = excessMmol / totalMl;
  steps.push(
    {
      id: 'excess',
      title: 'Neutralise',
      formula: acidExcess ? 'excess = n(H₃O⁺) − n(OH⁻)' : 'excess = n(OH⁻) − n(H₃O⁺)',
      working: acidExcess
        ? `${formatSci(acidMmol)} − ${formatSci(hydroxideMmol)} = ${formatSci(excessMmol)} mmol H₃O⁺ left over`
        : `${formatSci(hydroxideMmol)} − ${formatSci(acidMmol)} = ${formatSci(excessMmol)} mmol OH⁻ left over`,
      status: 'done',
    },
    {
      id: 'leftover',
      title: 'Spread through the total volume',
      formula: `[${ion}] = excess ÷ (V(acid) + V(base))`,
      working: `[${ion}] = ${formatSci(excessMmol)} mmol ÷ (${acidVolumeMl} + ${baseVolumeMl}) mL = ${conc(leftover)}`,
      status: 'done',
    },
  );
  return finishStrong(acidExcess ? 'acid' : 'base', leftover, lead, steps);
}

function calculateWeak(setup: AcidBaseExploreSetup): AcidBaseExploreResult {
  const species = weakSpecies(setup.weakId);
  if (!species) throw new Error('Choose a reviewed weak acid or base.');
  const c = setup.values.weakConcentration;
  const isBase = species.type === 'base';
  const kLabel = isBase ? 'Kb' : 'Ka';
  const ion = isBase ? 'OH⁻' : 'H₃O⁺';
  const approx = approximateIonised(species.k, c);
  const approximatePercent = (approx / c) * PERCENT;
  const valid = approximatePercent <= APPROXIMATION_LIMIT_PERCENT;
  const x = valid ? approx : exactIonised(species.k, c);
  const percentIonised = (x / c) * PERCENT;
  const lead = isBase
    ? `${species.formula} + H₂O ⇌ ${species.conjugate} + OH⁻`
    : `${species.formula} + H₂O ⇌ H₃O⁺ + ${species.conjugate}`;
  const steps: WorkedStep[] = [
    {
      id: 'k',
      title: `Reviewed ${kLabel} at 25 °C`,
      formula: `${kLabel} = x² ÷ (c − x), x = [${ion}]`,
      working: `${kLabel} = ${formatSci(species.k, 2)}, c = ${conc(c)}`,
      status: 'done',
    },
    {
      id: 'shortcut',
      title: 'Shortcut: assume c − x ≈ c',
      formula: `x ≈ √(${kLabel} × c)`,
      working: `x ≈ √(${formatSci(species.k, 2)} × ${formatSci(c)}) = ${conc(approx)}`,
      status: 'done',
    },
    {
      id: 'rule',
      title: 'Test the 5 % rule',
      formula: '% ionised = x ÷ c × 100',
      working: `${formatSci(approx)} ÷ ${formatSci(c)} × 100 = ${formatSci(approximatePercent)} % ${
        valid ? '≤ 5 %, so the shortcut is accepted' : '> 5 %, so the shortcut fails'
      }`,
      status: 'done',
    },
  ];
  if (!valid) {
    steps.push({
      id: 'quadratic',
      title: 'Solve the quadratic instead',
      formula: `x = (−${kLabel} + √(${kLabel}² + 4 × ${kLabel} × c)) ÷ 2`,
      working: `x = (−${formatSci(species.k, 2)} + √((${formatSci(species.k, 2)})² + 4 × ${formatSci(species.k, 2)} × ${formatSci(c)})) ÷ 2 = ${conc(x)} (${formatSci(percentIonised)} % ionised)`,
      status: 'done',
    });
  }
  const weakBase = { lead, percentIonised, approximation: { approximatePercent, valid }, weakSpeciesId: species.id };
  if (x < MIN_STRONG_CONCENTRATION) {
    return {
      ...weakBase,
      steps: [...steps, ...STOPPED_STEPS],
      modelPH: null,
      summary: 'Result: no pH. The model stops at its limit.',
      limitNote: limitNoteFor(ion, x),
    };
  }
  const finish = phFromIon(isBase ? 'base' : 'acid', x);
  return {
    ...weakBase,
    steps: [...steps, ...finish.steps],
    modelPH: finish.pH,
    summary: `Result: [${ion}] = ${conc(x)} · ${formatSci(percentIonised)} % ionised · pH = ${formatLog(finish.pH)}`,
    limitNote: null,
  };
}

/** Worked calculation for one learner setup. Throws a specific message on invalid input (fail closed). */
export function calculateAcidBaseExplore(setup: AcidBaseExploreSetup): AcidBaseExploreResult {
  const error = firstModelError(acidBaseExploreFields(setup.kind), setup.values);
  if (error) throw new Error(error);
  switch (setup.kind) {
    case 'strong':
      return calculateStrong(setup);
    case 'dilution':
      return calculateDilution(setup);
    case 'mixing':
      return calculateMixing(setup);
    default:
      return calculateWeak(setup);
  }
}

const byFormula = (formula: string) => STRONG_SOLUTES.find((solute) => solute.formula === formula)?.id;

/** Loads a reviewed problem's numbers into the Explore inputs, or null when it has no Explore shape. */
export function exploreSetupFromProblem(spec: AcidBasePhSpec, current: AcidBaseExploreSetup): AcidBaseExploreSetup | null {
  const values = { ...current.values };
  switch (spec.kind) {
    case 'strong-acid':
    case 'strong-base': {
      const id = byFormula(spec.formula);
      return id ? { ...current, kind: 'strong', strongId: id, values: { ...values, concentration: spec.concentration } } : null;
    }
    case 'dilution': {
      const id = byFormula(spec.formula);
      return id
        ? {
            ...current,
            kind: 'dilution',
            strongId: id,
            values: { ...values, concentration: spec.concentration, aliquotMl: spec.aliquotMl, finalMl: spec.finalMl },
          }
        : null;
    }
    case 'mixing': {
      const acidId = byFormula(spec.acid.formula);
      const baseId = byFormula(spec.base.formula);
      return acidId && baseId
        ? {
            ...current,
            kind: 'mixing',
            acidId,
            baseId,
            values: {
              ...values,
              acidConcentration: spec.acid.concentration,
              acidVolumeMl: spec.acid.volumeMl,
              baseConcentration: spec.base.concentration,
              baseVolumeMl: spec.base.volumeMl,
            },
          }
        : null;
    }
    case 'weak-acid-ph':
    case 'weak-acid-percent':
    case 'weak-base-ph':
      return { ...current, kind: 'weak', weakId: spec.speciesId, values: { ...values, weakConcentration: spec.concentration } };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------------------------
// Teaching goal (CT-INTERACT C7): one learning question and 3 tasks per Explore mode
// ---------------------------------------------------------------------------------------------

/** What the task checks read: the learner's setup and the model result it produced. */
export interface AcidBaseChallengeInput {
  setup: AcidBaseExploreSetup;
  result: AcidBaseExploreResult;
}

/** pH tolerance for a target-pH task: ± 0.05 pH unit (about ± 12 % in [H₃O⁺]). */
export const PH_TARGET_TOLERANCE = 0.05;
/** Accepted dilution factors for "dilute 10 times" (± 5 %). */
const TENFOLD_MIN = 9.5;
const TENFOLD_MAX = 10.5;
/** Accepted concentrations for "dilute ethanoic acid to 0.01 mol/L" (± 5 %). */
const WEAK_TENFOLD_MIN = 0.0095;
const WEAK_TENFOLD_MAX = 0.0105;
const TARGET_PH = 3;

const isStrongAcid = (id: string) => STRONG_SOLUTES.find((solute) => solute.id === id)?.type === 'acid';
const nearPH = (pH: number | null, target: number) => pH !== null && Math.abs(pH - target) <= PH_TARGET_TOLERANCE;

export const STRONG_PH_QUESTION = 'How does changing the amount of H₃O⁺ in a strong acid change its pH?';

export const STRONG_PH_CHALLENGES: ExploreChallenge<AcidBaseChallengeInput>[] = [
  {
    id: 'strong-ph-3',
    prompt: 'Make one strong acid solution with a pH of 3.00, changing only its concentration.',
    hint: '0.01 mol/L HCl has pH 2. How much less acid gives one pH unit more?',
    isMet: ({ setup, result }) => setup.kind === 'strong' && isStrongAcid(setup.strongId) && nearPH(result.modelPH, TARGET_PH),
    why: 'pH = −log₁₀[H₃O⁺], so 0.001 mol/L HCl has pH 3.00. One tenth of the acid raises the pH by only 1, not by 10.',
  },
  {
    id: 'dilute-tenfold',
    prompt: 'Dilute a strong acid exactly 10 times. Does the pH go up by 10, or by 1?',
    hint: 'Keep the aliquot at 10 mL and change the final volume.',
    isMet: ({ setup, result }) => {
      if (setup.kind !== 'dilution' || !isStrongAcid(setup.strongId) || result.modelPH === null) return false;
      const factor = setup.values.finalMl / setup.values.aliquotMl;
      return factor >= TENFOLD_MIN && factor <= TENFOLD_MAX;
    },
    why: '10 mL made up to 100 mL leaves one tenth of the H₃O⁺ in each litre, so the pH rises by exactly 1 (0.01 mol/L, pH 2 → 0.001 mol/L, pH 3). Diluting an acid never takes it past pH 7.',
  },
  {
    id: 'neutral-baoh2',
    prompt: 'Use Ba(OH)₂ to bring the mixture to exactly pH 7. Is the base volume the same as the acid volume?',
    hint: 'Count the OH⁻: n = c × V × 2 for Ba(OH)₂.',
    isMet: ({ setup, result }) => setup.kind === 'mixing' && setup.baseId === 'baoh2' && result.modelPH === NEUTRAL_PH,
    why: 'Each Ba(OH)₂ releases 2 OH⁻, so 12.5 mL of 0.1 mol/L Ba(OH)₂ neutralises 25 mL of 0.1 mol/L HCl. Neutral means equal moles of H₃O⁺ and OH⁻, not equal volumes.',
  },
];

export const WEAK_PH_QUESTION = 'Why does a weak acid have a higher pH than a strong acid of the same concentration?';

export const WEAK_PH_CHALLENGES: ExploreChallenge<AcidBaseChallengeInput>[] = [
  {
    id: 'weak-tenfold',
    prompt: 'Dilute ethanoic acid 10 times, to 0.01 mol/L. Does its pH rise by a whole unit?',
    hint: 'The default is 0.1 mol/L ethanoic acid at pH 2.87.',
    isMet: ({ setup, result }) =>
      result.weakSpeciesId === 'ethanoic' &&
      result.modelPH !== null &&
      setup.values.weakConcentration >= WEAK_TENFOLD_MIN &&
      setup.values.weakConcentration <= WEAK_TENFOLD_MAX,
    why: 'x = √(Ka × c), so one tenth of the acid gives only 1/√10 of the H₃O⁺: the pH goes from 2.87 to 3.37, up by 0.5. A weak acid ionises more when it is diluted (1.3 % → 4.2 %).',
  },
  {
    id: 'shortcut-fails',
    prompt: 'Make the 5 % shortcut fail, so the model has to solve the quadratic.',
    hint: 'Try a more dilute solution, or a weak acid with a larger Ka such as HF.',
    isMet: ({ result }) => result.approximation !== null && !result.approximation.valid,
    why: 'The shortcut assumes c − x ≈ c. A very dilute solution or a larger Ka ionises a bigger share (0.1 mol/L HF is about 8 % ionised), so x is no longer small next to c.',
  },
  {
    id: 'weak-matches-strong',
    prompt: 'Make a weak acid solution with the same pH as 0.001 mol/L HCl (pH 3.00).',
    hint: 'For ethanoic acid, c ≈ x² ÷ Ka with x = 0.001 mol/L.',
    isMet: ({ setup, result }) => weakSpecies(setup.weakId)?.type === 'acid' && nearPH(result.modelPH, TARGET_PH),
    why: 'About 0.056 mol/L ethanoic acid has pH 3.00 (56 times the concentration of the HCl) because only about 2 % of it ionises. Strength (how much ionises) is not the same as concentration.',
  },
];
